
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingCart, Settings } from 'lucide-react';
import { UnitSystem } from '@/utils/unitConversion/types';
import { format } from 'date-fns';

interface ShoppingListHeaderProps {
  preferredUnits: UnitSystem;
  adults: number;
  kids: number;
  selectedDaysCount: number;
  mealTypeCounts: {
    breakfast: number;
    lunch: number;
    dinner: number;
  };
  recipeCount: number;
  metadata?: {
    generatedAt: string;
    totalItems: number;
    generatedBy?: string;
  };
  onShoppingModeToggle?: () => void;
  onEditSettings?: () => void;
}

const ShoppingListHeader: React.FC<ShoppingListHeaderProps> = ({
  preferredUnits,
  adults,
  kids,
  selectedDaysCount,
  mealTypeCounts,
  recipeCount,
  metadata,
  onShoppingModeToggle,
  onEditSettings,
}) => {
  const formatGeneratedTime = () => {
    if (!metadata?.generatedAt) return 'Created recently';
    const generatedDate = new Date(metadata.generatedAt);
    const byName = metadata.generatedBy ? `by ${metadata.generatedBy}, ` : '';
    return `Created ${byName}${format(generatedDate, 'MMM d, h:mm a')}`;
  };

  return (
    <div className="p-4 bg-slate-50 border-b">
      <div className="space-y-3">
        {/* Title and Regenerate Button */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-lg font-semibold text-foreground leading-tight">
              {formatGeneratedTime()}
            </h3>
          </div>
            <div className="flex gap-2 shrink-0">
              {onEditSettings && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onEditSettings} 
                  title="Edit settings"
                >
                  <Settings className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Edit</span>
                </Button>
              )}
              {onShoppingModeToggle && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={onShoppingModeToggle} 
                  title="Enter shopping mode"
                >
                  <ShoppingCart className="h-3 w-3 mr-1" />
                  <span className="hidden sm:inline">Shop</span>
                </Button>
              )}
            </div>
          </div>

          {/* Info Badges */}
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-xs">
              {selectedDaysCount} {selectedDaysCount === 1 ? 'day' : 'days'}
            </Badge>
            {mealTypeCounts.breakfast > 0 && (
              <Badge variant="outline" className="text-xs">
                {mealTypeCounts.breakfast} breakfast{mealTypeCounts.breakfast !== 1 ? 's' : ''}
              </Badge>
            )}
            {mealTypeCounts.lunch > 0 && (
              <Badge variant="outline" className="text-xs">
                {mealTypeCounts.lunch} lunch{mealTypeCounts.lunch !== 1 ? 'es' : ''}
              </Badge>
            )}
            {mealTypeCounts.dinner > 0 && (
              <Badge variant="outline" className="text-xs">
                {mealTypeCounts.dinner} dinner{mealTypeCounts.dinner !== 1 ? 's' : ''}
              </Badge>
            )}
            <Badge variant="outline" className="text-xs">
              {recipeCount} recipe{recipeCount !== 1 ? 's' : ''}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {adults} adults, {kids} kids
            </Badge>
            <Badge variant="outline" className="text-xs">
              {preferredUnits === 'metric' ? 'Metric' : 'Imperial'} units
            </Badge>
          </div>
        </div>
      </div>
  );
};

export default ShoppingListHeader;
